import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Badge from "../BestFitCard/Badge";
import "../BestFitCard/Badge.scss";
import { CTA } from "@coverme/forms";
import { Icon } from "@coverme/icons";
import { TooltipMessage, TooltipTrigger } from "@coverme/elements";
import { Tooltip } from "../../components/Tooltips/Tooltip";
import "@coverme/elements/dist/styles/Tooltip.scss";
import { CheckMarkCircleIcon } from "../Icons/CheckMarkCircleIcon";
import { CrossMarkCircleIcon } from "../Icons/CrossMarkCircleIcon";

function getVerifications(verification, responsibleParty) {
    const data = verification.data;
    const identityVerificationResult =
        data["nameVerification"]?.result === "Verified" &&
        data["ssnVerification"]?.result === "Verified" &&
        data["dateOfBirthVerification"]?.result === "Verified";

    const residencyVerification = data["addressVerification"]?.result === "Verified";

    return {
        identity: identityVerificationResult,
        residency: residencyVerification,
        citizenship: data["ssnVerification"]?.result === "Verified" && responsibleParty?.isUsCitizen === true,
    };
}

export const BestFitCard = ({
    program,
    onApprove,
    onDeny,
    detailsOpen = false,
    toggleDetails,
    canApprove,
    canDeny = true,
    approveButtonText = "Approve",
    overallScore = 0,
    fitScore = 0,
    riskScore = 0,
    reliabilityScore = 0,
    caseClues,
    verifications,
    responsibleParty,
}) => {
    const { programDetails } = program;
    const { Title, Line1, Line1a, Line2, Line2a, Line3, SubTitle } = programDetails;
    const editedCaseClues = useMemo(() => {
        if (caseClues != null && caseClues?.length > 0 && Title.includes("Cash")) {
            let filteredCaseClues = caseClues?.filter(x => !x.includes("Allowable income"));
            filteredCaseClues?.push("Allowable income: N/A");
            return filteredCaseClues;
        } else {
            return caseClues;
        }
    }, [caseClues, Title]);

    const verificationResults = useMemo(() => {
        return getVerifications(verifications, responsibleParty);
    }, [verifications]);

    return (
        <div>
            <div className={`best-fit-card-new ${detailsOpen && "open"}`}>
                <div className="best-fit-card-new__border" />
                <div className="best-fit-card-new__badge">
                    <Badge color="blue" label="Best Fit" />
                </div>
                <div className="best-fit-card-new__container">
                    <div className="best-fit-card-new__left">
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <h1 className="best-fit-card-new__title">{Title}</h1>
                        </div>
                        <div className="best-fit-card-new__cta">
                            {canApprove && (
                                <>
                                    <CTA size={"medium"} ctaType={"button"} styleType={"blue"} click={onApprove}>
                                        {approveButtonText}
                                    </CTA>
                                </>
                            )}
                            {!canApprove && (
                                <p style={{ textAlign: "center" }}>
                                    <small>
                                        <p>
                                            <small>
                                                <p>APPROVAL REQUIRED</p>
                                                Case has been forwarded to a supervisor for further review
                                            </small>
                                        </p>
                                    </small>
                                </p>
                            )}
                        </div>
                        {editedCaseClues !== null && editedCaseClues?.length > 0 && (
                            <div className="case-clues-container">
                                <div>
                                    <h3>Case Clues</h3>
                                </div>
                                <div className="case-clues">
                                    <ul>
                                        {editedCaseClues.map((x, i) => {
                                            return (
                                                <>
                                                    <li key={`caseClue_${i}`}>
                                                        <p>{x}</p>
                                                    </li>
                                                </>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="best-fit-card-new__right">
                        <div className="best-fit-card-new__scores_container">
                            <h3>
                                Confidence Scores
                                <Tooltip>
                                    <TooltipTrigger>
                                        <Icon icon="tooltip" />
                                    </TooltipTrigger>
                                    <TooltipMessage>
                                        <p>
                                            The CoverMe Scoring Solution combines patient information with detailed
                                            third-party data to identify and rank the best financial programs available
                                            for the patient on the basis of Fit, Risk, and Reliability.
                                            <br />
                                            <br />
                                            <b>The Confidence Score</b>, is an overall blend of Fit, Risk, and
                                            Reliability sub-scores.
                                            <br />
                                            <br />
                                            <b>The Fit Score</b> is based on the likely eligibility of the patient for
                                            any individual program. Each patient’s likelihood of eligibility is based on
                                            the rules and regulations of each program.
                                            <br />
                                            <br />
                                            <b>The Risk Score</b> weighs an average of third-party risk score rankings
                                            that use financial and households factors to define ability and willingness
                                            to pay healthcare expenses.
                                            <br />
                                            <br />
                                            <b>The Reliability Score</b> factors the reliability of data used to inform
                                            the Fit and Risk Scores. In addition, the Reliability Score looks at the
                                            reliability of individual programs.
                                            <br />
                                            <br />
                                            The scores come together in the CoverMe Marketplace, giving providers a
                                            ranked list of programs, featuring the Best Fit program for individual
                                            patients based on their personal profile. The Best Fit program will always
                                            have the highest Fit Score.
                                        </p>
                                    </TooltipMessage>
                                </Tooltip>
                            </h3>
                            <div className="best-fit-card-new__scores">
                                <div>
                                    <ScoreComponent score={overallScore} title="" isPrimaryScore={true} />
                                </div>
                            </div>
                        </div>
                        <div className="best-fit-card-new__verification-container">
                            <div className="verification-item">
                                {verificationResults.identity && <CheckMarkCircleIcon />}
                                {!verificationResults.identity && <CrossMarkCircleIcon />}
                                <p>Identity</p>
                            </div>
                            <div className="verification-item">
                                {verificationResults.residency && <CheckMarkCircleIcon />}
                                {!verificationResults.residency && <CrossMarkCircleIcon />}
                                <p>Residency</p>
                            </div>
                            <div className="verification-item">
                                {verificationResults.citizenship && <CheckMarkCircleIcon />}
                                {!verificationResults.citizenship && <CrossMarkCircleIcon />}
                                <p>Citizenship</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="best-fit-card-new__placeholder" />
            <div className="best-fit-card-new__more">
                <svg
                    className="best-fit-card-new__triangle"
                    width="28px"
                    height="14px"
                    viewBox="0 0 28 14"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <g id="Marketplace-(Approved)" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                        <g id="CM--Marketplace" transform="translate(-193.000000, -1255.000000)" fillRule="nonzero">
                            <g id="Group" transform="translate(197.000000, 1256.000000)">
                                <path
                                    d="M11.4142136,1.41421356 L20,10 L0,10 L8.58578644,1.41421356 C9.36683502,0.633164979 10.633165,0.633164979 11.4142136,1.41421356 Z"
                                    id="Triangle"
                                    stroke="#51617D"
                                    strokeWidth="3"
                                />
                                <polygon
                                    id="Rectangle"
                                    fill="#FFFFFF"
                                    points="3.62203749 8.49553311 16.3712082 8.49553311 20.1947424 12.233536 1.24344979e-14 12.416808"
                                />
                            </g>
                        </g>
                    </g>
                </svg>
                <span className="best-fit-card-new__close" onClick={() => toggleDetails()}>
                    &#215;
                </span>
            </div>
        </div>
    );
};

function ScoreComponent({ score, title, isPrimaryScore }) {
    let className = "";
    if (score > 71) {
        className = "green";
    } else if (score >= 41) {
        className = "yellow";
    } else {
        className = "red";
    }

    return (
        <div className={`best-fit-card-new__scores__score ${isPrimaryScore ? "primary-score" : ""}`}>
            <div className="score-value-container">
                <div className={`score-value ${className} `}>{score}</div>
            </div>
            <div className="score-title">{title}</div>
        </div>
    );
}

BestFitCard.propTypes = {
    onApprove: PropTypes.func,
    onDeny: PropTypes.func,
    canApprove: PropTypes.bool,
    caseClues: PropTypes.array,
    program: PropTypes.shape({
        programDetails: PropTypes.shape({
            Title: PropTypes.string,
            Line1: PropTypes.string,
            Line1a: PropTypes.string,
            Line2: PropTypes.string,
            Line2a: PropTypes.string,
            Line3: PropTypes.string,
        }).isRequired,
    }).isRequired,
};

export default BestFitCard;
